// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { sprintf } from 'sprintf-js';
import styled from 'styled-components';
import type { CurrentUser, UserOrgMini, UserGroupMini } from 'api/currentUser';
import { partialUpdateOrg as apiUpdateOrg } from 'api/orgAdmin';
import type { ImageData } from 'api/shared';
import type { Org as ApiOrg } from 'api/orgs';
import type { ActionType, VolopImportedType } from 'api/volops';
import { getIn } from 'utils/functional';
import {
  VOLOP_IMPORTED_TYPE_MAP,
  LISTING_TYPE_URL_SEGMENT_MAP
} from 'utils/constants';
import Box from 'components/Box';
import { trackClicked } from 'utils/analytics';
import {
  ButtonBase,
  PrimaryButtonRouterLink,
  PrimaryButton
} from 'components/Button';
import Divider from 'components/Divider';
import FundingMeter from 'components/FundingMeter';
import Icon from 'components/Icon';
import VolopIcon from 'components/VolopIcon';
import { LogoImage } from 'components/Image';
import { Link as ReactRouterLink } from 'components/RouterLink';
import { withRouter } from 'react-router';
import type { RouterHistory } from 'react-router';
import { getCityStateString } from 'utils/address';
import {
  distanceInWordsToNow,
  shorterDate,
  shorterTime,
  shortTimezone,
  combineDateTime
} from 'utils/date';
import scrollElementIntoView from 'utils/scrollIntoView';
import { retinaBackgroundImage } from 'utils/style';
import { imageUrl as getImageUrl } from 'utils/filestack';
import ListingTypeLabel from 'components/ListingTypeLabel';
import { H4, BodySmall, BodyMicro } from 'components/Text';
import ListingStatusLabel from 'components/ListingStatusLabel';
import Hide from 'components/Hide';
import SearchTile from 'components/SearchTile';
import Form, { LogoPickerField } from 'components/Form';
import { NAME_MAP } from 'layouts/ListingLayout';

import HideListingContainer from 'containers/HideListingContainer';
import HideGroupVolopContainer from 'containers/HideGroupVolopContainer';
import { partialUpdateOrg as partialUpdateOrgRaw } from '../redux/main/actions/orgs';
import { breakpoints, colors, transition } from '../theme';

const { useRef, useEffect } = React;

const IMAGE_WIDTH = 120;

type Hit = {
  name: string,
  type:
    | 'JOB'
    | 'INTERNSHIP'
    | 'VOLOP'
    | 'IDEALIST_DAY_EVENT'
    | 'ORG'
    | 'GROUP'
    | 'IMPORTED',
  published?: number,
  city: string,
  state: string,
  stateStr: string,
  country: string,
  url: { [UserLocale]: string } | string,
  startDate?: string,
  startTime?: string,
  endDate?: string,
  endTime?: string,
  localizedStarts?: string,
  localizedEnds?: string,
  timezone?: string,
  orgUrl?: { [UserLocale]: string },
  groupName?: string,
  groupUrl?: { [UserLocale]: string },
  objectID: string,
  orgID?: string,
  groupID?: string,
  orgName?: string,
  'data-qa-id'?: string,
  source?: VolopImportedType,
  actionType?: ActionType,
  isFullTime?: boolean,
  isVirtual?: boolean,
  remoteOk?: boolean,
  paid?: boolean,
  amountRequested: number,
  amountRaised: number,
  goal: number,
  signatureCount: number,
  logo: ImageData,
  imageUrl?: string,
  imageHandle?: string,
  hasPublishedListings?: boolean,
  hasAts?: boolean
};

const Actions = styled(Box)`
  @media (min-width: ${breakpoints[1]}) {
    border-left: 1px solid ${colors.aluminum};
  }
`;

const filestackTransform = (width: number, height: number) =>
  `resize=width:${width},height:${height},fit:crop,align:center/quality=value:70`;

const SmallImage = styled(Box)`
  ${props =>
    props.$url &&
    `
    background-image: url('${props.$url}');
    `}
  ${props =>
    props.$handle &&
    retinaBackgroundImage(
      getImageUrl({
        handle: props.$handle,
        transform: filestackTransform(IMAGE_WIDTH, IMAGE_WIDTH)
      }),
      {
        filename2x: getImageUrl({
          handle: props.$handle,
          transform: filestackTransform(IMAGE_WIDTH * 2, IMAGE_WIDTH * 2)
        }),
        include3x: false
      }
    )}
  img {
    display: block;
  }
`;

const ActionButtons = ({
  hit,
  org,
  group,
  history,
  type
}: {
  hit: Object,
  org: ?UserOrgMini,
  group: ?UserGroupMini,
  history: RouterHistory,
  type: string
}) => {
  const viewUrl =
    type === 'ORG' && org
      ? `/admin/${org.id}`
      : type === 'GROUP' && group
      ? `/group-admin/${group.id}`
      : type === 'VOLOP' && group
      ? `/group-admin/${group.id}/${LISTING_TYPE_URL_SEGMENT_MAP[hit.type]}/${
          hit.objectID
        }`
      : org
      ? `/admin/${org.id}/${LISTING_TYPE_URL_SEGMENT_MAP[hit.type]}/${
          hit.objectID
        }`
      : '';
  return (
    <>
      <PrimaryButtonRouterLink
        to={`${viewUrl}/edit`}
        data-qa-id="edit-button"
        size="small"
      >
        {getText('Edit')}
      </PrimaryButtonRouterLink>
      {type !== 'ORG' && type !== 'GROUP' && (
        <>
          {org && (
            <>
              <HideListingContainer
                listingId={hit.objectID}
                listingType={hit.type}
                orgId={org.id}
                render={({ hideListing, submitting }) => (
                  <PrimaryButton
                    onClick={e => {
                      e.stopPropagation();
                      hideListing().then(() => {
                        history.push(viewUrl, {
                          flashMessage: { type: 'LISTING_HIDDEN' }
                        });
                      });
                    }}
                    disabled={submitting}
                    data-qa-id="hide-button"
                    size="small"
                  >
                    {/* TRANSLATORS: hide listing, button text */ getText(
                      'Hide'
                    )}
                  </PrimaryButton>
                )}
              />
              <PrimaryButtonRouterLink
                to={`${viewUrl}/repost`}
                data-qa-id="repost-button"
                size="small"
                disabled={hit.type === 'VOLOP' && org && !org.canPostVolops}
              >
                {getText('Repost')}
              </PrimaryButtonRouterLink>
            </>
          )}
          {group && (
            <>
              <HideGroupVolopContainer
                volopId={hit.objectID}
                groupId={group.id}
                render={({ hideVolop, submitting }) => (
                  <PrimaryButton
                    onClick={e => {
                      e.stopPropagation();
                      hideVolop().then(() => {
                        history.push(viewUrl, {
                          flashMessage: { type: 'LISTING_HIDDEN' }
                        });
                      });
                    }}
                    disabled={submitting}
                    data-qa-id="hide-button"
                    size="small"
                  >
                    {/* TRANSLATORS: hide listing, button text */ getText(
                      'Hide'
                    )}
                  </PrimaryButton>
                )}
              />
              <PrimaryButtonRouterLink
                to={`${viewUrl}/repost`}
                data-qa-id="repost-button"
                size="small"
              >
                {getText('Repost')}
              </PrimaryButtonRouterLink>
            </>
          )}
        </>
      )}
    </>
  );
};

const getCtaByType = ({
  type,
  baseUrl,
  hasPublishedListings,
  userHasApplied,
  hasAts
}: {
  type: string,
  baseUrl: string,
  hasPublishedListings?: boolean,
  userHasApplied: boolean,
  hasAts?: boolean
}) => {
  if (['ORG', 'GROUP'].includes(type) && !hasPublishedListings) {
    return {
      text: getText('View'),
      url: baseUrl,
      analyticsText: 'View'
    };
  }
  switch (type) {
    case 'IDEALIST_DAY_EVENT':
      return {
        text: getText('Join Us'),
        url: `${baseUrl}#section-rsvp`,
        analyticsText: 'Join Us'
      };
    case 'ORG':
      return {
        text: getText('See Listings'),
        url: `${baseUrl}#section-opportunities`,
        analyticsText: 'See Listings'
      };
    case 'GROUP':
      return {
        text: getText('See Listings'),
        url: `${baseUrl}#section-opportunities`,
        analyticsText: 'See Listings'
      };
    case 'IMPORTED': {
      const importedUrl = `${baseUrl}${
        baseUrl.indexOf('?') > -1 ? '&' : '?'
      }utm_source=idealist&utm_medium=referral&utm_campaign=action_opportunities`;
      return {
        text: getText('Visit'),
        url: importedUrl,
        analyticsText: 'Visit'
      };
    }
    case 'JOB':
    case 'VOLOP':
    case 'INTERNSHIP':
    default:
      return userHasApplied
        ? {
            text: (
              <>
                <Box color={colors.turtleGreen}>
                  <Icon
                    stroke="currentColor"
                    name="checkmark-path"
                    width={12}
                    height={12}
                    display="block"
                  />
                </Box>
                <Box color={colors.battleshipGrey}>{getText('Applied')}</Box>
              </>
            ),
            url: baseUrl,
            analyticsText: 'Applied'
          }
        : {
            text: hasAts ? getText('Easy Apply') : getText('Apply'),
            url: `${baseUrl}#section-apply`,
            analyticsText: hasAts ? 'Easy Apply' : getText('Apply')
          };
  }
};

const StyledButton = styled(({ saved, ...props }: { saved?: boolean }) => (
  <ButtonBase {...props} size="small" />
))`
  z-index: 2;
  box-sizing: border-box;
  display: flex;
  padding-left: 0;
  padding-right: 0;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  transition: all ${transition};
  svg * {
    transition-duration: ${transition};
    transition-property: stroke, fill;
  }
  svg {
    margin-right: 4px;
  }
  @media (min-width: ${breakpoints[1]}) {
    width: 100%;
  }
`;

const BaseCtaButton = ({
  external,
  url,
  ...props
}: {
  external: boolean,
  url: string
}) =>
  external ? (
    <StyledButton component="a" href={url} {...props} target="_blank" />
  ) : (
    <StyledButton component={ReactRouterLink} to={url} {...props} />
  );

const CtaButton = styled(
  ({
    hit,
    isImported,
    applications,
    ...props
  }: {
    hit: Hit,
    isImported: boolean,
    applications?: Array<string>
  }) => {
    const cta = getCtaByType({
      type: hit.type,
      baseUrl: typeof hit.url === 'string' ? hit.url : hit.url[USER_LOCALE],
      hasPublishedListings: hit.hasPublishedListings,
      userHasApplied: !!(applications && applications.includes(hit.objectID)),
      hasAts: hit.hasAts
    });

    return (
      <BaseCtaButton
        {...props}
        external={isImported}
        url={cta.url}
        onClick={() => {
          trackClicked('Search results CTA button', { type: hit.type });
        }}
      >
        <Box display="flex" alignItems="center">
          {cta.text}
          {isImported && (
            <Box ml="4px">
              <Icon
                width={12}
                height={12}
                stroke="currentColor"
                strokeWidth="8px"
                display="block"
                name="external-link"
              />
            </Box>
          )}
        </Box>
      </BaseCtaButton>
    );
  }
)`
  &:hover {
    color: white;
    background: ${colors.waterBlue};
    * {
      color: white;
    }
  }
`;

const SaveButton = styled(StyledButton)`
  color: ${colors.battleshipGrey};
  svg {
    * {
      fill: none;
      stroke: currentColor;
      stroke-width: 2px;
    }
  }
  &:hover {
    background: ${colors.platinum};
    svg {
      * {
        fill: ${colors.turtleGreen};
        stroke: ${colors.turtleGreen};
      }
    }
  }
  ${props =>
    props.saved &&
    `
    svg {
      * {
        fill: ${colors.turtleGreen};
        stroke: ${colors.turtleGreen};
      }
    }
  `}
`;

const RouterLink = styled(ReactRouterLink)`
  &,
  &:active,
  &:visited {
    color: inherit;
    text-decoration: none;
    position: relative;
    z-index: 2;
  }
  &:hover {
    color: inherit;
    text-decoration: underline;
  }
`;

const Link = styled.a`
  &,
  &:active,
  &:visited {
    color: inherit;
    text-decoration: none;
    position: relative;
    z-index: 2;
  }
  &:hover {
    color: inherit;
    text-decoration: underline;
  }
`;

export const confirmModal = {
  type: 'danger',
  title: getText('Remove Image'),
  buttonText: getText('Remove'),
  body: getText('Are you sure you want to remove this image?')
};

const SearchTileContainer = styled(Box)`
  &:hover,
  &:focus {
    ${SearchTile} {
      &::before {
        transform: translateY(0);
      }
    }
  }
  * {
    pointer-events: none;
  }
  a,
  button {
    pointer-events: auto;
  }
`;

export const SearchTileLink = styled(
  ({
    url,
    trackClick,
    ...props
  }: {
    url: string | Object,
    trackClick?: Function
  }) =>
    typeof url === 'object' ? (
      <ReactRouterLink
        {...props}
        to={{
          pathname: url[USER_LOCALE],
          state: {
            showSearchContext: true,
            originatingPathname: window.location.pathname
          }
        }}
        onClick={() => {
          if (trackClick) trackClick();
        }}
        tabIndex="-1"
      />
    ) : (
      <a
        {...props}
        href={`${url}${
          url.indexOf('?') > -1 ? '&' : '?'
        }utm_source=idealist&utm_medium=referral&utm_campaign=action_opportunities`}
        onClick={() => {
          if (trackClick) trackClick();
        }}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex="-1"
      />
    )
)`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: block;
  &:focus {
    outline: 0;
  }
`;

const LogoContainer = styled(Box)`
  ${props =>
    props.$showLogoUpload &&
    `
    z-index: 2;
    pointer-events: auto;
    * {
      pointer-events: auto;
    }
  `}
  img {
    max-height: 90px;
  }
`;

type OwnProps = {
  hit: Hit,
  scrollIntoView?: boolean,
  saveListing?: () => void,
  unsaveListing: () => void,
  saved: boolean,
  canSave: boolean,
  'data-qa-id'?: string,
  history: RouterHistory,
  trackClick?: () => void,
  showLabel?: boolean,
  hideCta?: boolean,
  disabled?: boolean,
  application: {
    status: 'ACTIVE' | 'INACTIVE',
    created: string
  },
  applications?: Array<string>,
  currentUser: CurrentUser
};

type Props = {
  ...$Exact<OwnProps>,
  partialUpdateOrg: Object => void,
  ownedOrgsById: { [string]: ApiOrg }
};

const SearchHit = React.memo(
  ({
    hit,
    saveListing,
    unsaveListing,
    saved,
    canSave,
    history,
    trackClick,
    scrollIntoView,
    currentUser,
    partialUpdateOrg,
    ownedOrgsById,
    showLabel,
    hideCta,
    disabled,
    application,
    applications,
    'data-qa-id': qaId
  }: Props) => {
    const hitRef = useRef(null);

    const {
      name,
      type,
      published,
      logo: searchResultLogo,
      imageHandle,
      imageUrl,
      orgUrl,
      groupName,
      groupUrl,
      isFullTime,
      isVirtual,
      remoteOk,
      paid,
      city,
      state,
      stateStr,
      country,
      url,
      startDate,
      startTime,
      endDate,
      endTime,
      localizedStarts,
      localizedEnds,
      timezone,
      objectID,
      amountRaised,
      amountRequested,
      signatureCount,
      goal,
      source,
      actionType
    } = hit;

    const amountCurrent = amountRaised || signatureCount;
    const amountTotal = amountRequested || goal;

    useEffect(
      () => {
        if (scrollIntoView && hitRef && hitRef.current) {
          scrollElementIntoView(hitRef.current);
        }
      },
      [scrollIntoView]
    );
    const updateOrg = values => {
      apiUpdateOrg(values).then(partialUpdateOrg);
    };

    const location = getCityStateString({
      city,
      stateCode: state || stateStr,
      country
    });
    const isImported = type === 'IMPORTED';

    const listingInfo = [];
    if (isFullTime !== undefined) {
      listingInfo.push(
        isFullTime ? getText('Full Time') : getText('Part Time')
      );
    }
    if (remoteOk !== undefined || isVirtual !== undefined) {
      listingInfo.push(
        isVirtual
          ? getText('Remote')
          : remoteOk
          ? 'Remote Possible'
          : getText('On-site')
      );
    }
    if (paid !== undefined) {
      listingInfo.push(paid ? getText('Paid') : getText('Unpaid'));
    }
    if (isImported && hit.source) {
      listingInfo.push(VOLOP_IMPORTED_TYPE_MAP[hit.source]);
    }
    if (actionType && !isImported) {
      listingInfo.push(
        <Box
          data-qa-id="listing-status"
          display="inline-block"
          py="4px"
          px="6px"
          bg={colors.waterBlue}
          color="white"
          borderRadius="4px"
          lineHeight={1}
          letterSpacing="0.25px"
          fontSize={[11, null, 13]}
        >
          Idealist
        </Box>
      );
    }

    const showCta =
      !hideCta &&
      [
        'IDEALIST_DAY_EVENT',
        'GROUP',
        'ORG',
        'JOB',
        'VOLOP',
        'INTERNSHIP',
        'IMPORTED'
      ].includes(type);

    const orgId = type === 'ORG' ? objectID : hit.orgID;
    const orgName = type === 'ORG' ? name : hit.orgName;
    const groupId = type === 'GROUP' ? objectID : hit.groupID;
    const ownerOrg =
      currentUser &&
      currentUser.orgs &&
      currentUser.orgs.find(o => o.id === orgId);
    const ownerGroup =
      currentUser &&
      currentUser.groups &&
      currentUser.groups.find(g => g.id === groupId);
    const logo =
      getIn(ownedOrgsById, [orgId || '', 'logo']) || searchResultLogo;
    const showLogoUpload = ownerOrg && !logo;

    const startDateTime =
      startDate && startTime
        ? combineDateTime(startDate, startTime)
        : localizedStarts || null;
    const endDateTime =
      endDate && endTime
        ? combineDateTime(endDate, endTime)
        : localizedEnds || null;

    return (
      <div ref={hitRef} data-qa-id={qaId || 'search-result'}>
        {(ownerOrg || ownerGroup) && (
          <Box
            display={['block', 'flex']}
            alignItems="center"
            justifyContent="space-between"
            p={['12px 20px', '16px 4px 16px 20px', '16px 12px 16px 30px']}
            zIndex="3"
            color="white"
            bg={colors.waterBlue}
            borderRadius="4px 4px 0 0"
            mb={-10}
          >
            <BodySmall>
              {type === 'ORG'
                ? getText('You are an administrator of this organization')
                : type === 'GROUP'
                ? getText('You are an administrator of this group')
                : type === 'VOLOP' && ownerGroup
                ? getText('Your group posted this volunteer opportunity')
                : sprintf(
                    getText(
                      'Your organization posted this %(listingTypeName)s'
                    ),
                    {
                      listingTypeName: NAME_MAP[type]
                    }
                  )}
            </BodySmall>
            <Box mt={['8px', 0]} ml={[-16, 0]} flex="0 0 auto">
              <ActionButtons
                type={type}
                org={ownerOrg}
                group={ownerGroup}
                hit={hit}
                history={history}
              />
            </Box>
          </Box>
        )}
        <SearchTileContainer
          position="relative"
          my={10}
          color={disabled && colors.battleshipGrey}
          cursor={disabled ? 'auto' : 'pointer'}
        >
          {!disabled && (
            <SearchTileLink
              trackClick={trackClick}
              url={url}
              aria-hidden="true"
            />
          )}
          <SearchTile
            pr={[undefined, null, '16px !important']}
            my="0 !important"
            borderRadius={ownerOrg && '0 0 4px 4px !important'}
            disabled={disabled}
          >
            <Box display={['block', null, 'flex']} pr={[undefined, 0, 0, 0]}>
              <Box
                display="flex"
                alignItems="flex-start"
                width="100%"
                flex="1 1 auto"
                pr={32}
                flexDirection={['column', 'row', null, null]}
              >
                <Box
                  flex="0 0 auto"
                  pr={[0, 30]}
                  pb={[24, 0]}
                  display={[isImported || !logo ? 'none' : 'flex', 'flex']}
                >
                  {isImported && !imageUrl && source ? (
                    <VolopIcon
                      name={source.toLowerCase().replace(/_/g, '-')}
                      width={IMAGE_WIDTH}
                      maxHeight={90}
                      m="0 auto"
                    />
                  ) : imageUrl || imageHandle ? (
                    <SmallImage
                      $url={imageUrl}
                      $handle={imageHandle}
                      width={IMAGE_WIDTH}
                      height={IMAGE_WIDTH}
                      backgroundPosition="center"
                      backgroundSize="cover"
                      borderRadius="4px"
                    />
                  ) : (
                    <LogoContainer
                      position="relative"
                      pointerEvents={showLogoUpload ? 'auto' : undefined}
                      $showLogoUpload={showLogoUpload}
                    >
                      {showLogoUpload ? (
                        <Form
                          onSubmit={() => undefined}
                          render={({ getValue, updateValues }) => {
                            const logoId = `logo-${objectID}`;
                            const image = ((getValue(logoId): any): ImageData);
                            return image ? (
                              <Box data-qa-id="logo-uploaded-image">
                                <LogoImage
                                  alignItems="flex-start"
                                  width={IMAGE_WIDTH}
                                  handle={image ? image.handle : undefined}
                                  mimetype={image ? image.mimetype : undefined}
                                  alt={name}
                                  initial={orgName || groupName || name}
                                />
                              </Box>
                            ) : (
                              <Box my={-12}>
                                <LogoPickerField
                                  name={logoId}
                                  altText={orgName}
                                  centerButton
                                  onChange={(newLogo: ImageData) => {
                                    updateValues({ [logoId]: newLogo });
                                    updateOrg({ id: orgId, logo: newLogo });
                                  }}
                                  message={getText('Upload Logo')}
                                  confirmModal={confirmModal}
                                  size={92}
                                  buttonOnly
                                  previewHeight={IMAGE_WIDTH}
                                  previewWidth={IMAGE_WIDTH}
                                  hideRemove
                                  data-qa-id="logo"
                                />
                              </Box>
                            );
                          }}
                        />
                      ) : (
                        <LogoImage
                          data-qa-id="logo-uploaded-image"
                          alignItems="flex-start"
                          width={IMAGE_WIDTH}
                          handle={logo ? logo.handle : undefined}
                          mimetype={logo ? logo.mimetype : undefined}
                          alt={name}
                          initial={orgName || groupName || name}
                        />
                      )}
                    </LogoContainer>
                  )}
                </Box>
                <Box width="100%" flex="1 1 auto">
                  {showLabel && (
                    <Box mb={14}>
                      <ListingTypeLabel
                        listingType={type}
                        actionType={actionType}
                      />
                    </Box>
                  )}
                  <H4 mt={0} mb="4px" lineHeight={1.375}>
                    {disabled ? (
                      <>
                        {name.slice(0, 120)}
                        {name.length > 120 && (
                          <span
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: '&hellip;' }}
                          />
                        )}
                      </>
                    ) : typeof url === 'object' ? (
                      <RouterLink
                        data-qa-id="search-result-link"
                        to={{
                          pathname: url[USER_LOCALE],
                          state: {
                            showSearchContext: true,
                            originatingPathname: window.location.pathname
                          }
                        }}
                        onClick={() => {
                          if (trackClick) trackClick();
                        }}
                      >
                        {name.slice(0, 120)}
                        {name.length > 120 && (
                          <span
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: '&hellip;' }}
                          />
                        )}
                      </RouterLink>
                    ) : (
                      typeof url === 'string' && (
                        <Link
                          data-qa-id="search-result-link"
                          href={`${url}${
                            url.indexOf('?') > -1 ? '&' : '?'
                          }utm_source=idealist&utm_medium=referral&utm_campaign=action_opportunities`}
                          onClick={() => {
                            if (trackClick) trackClick();
                          }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {name.slice(0, 120)}
                          {name.length > 120 && (
                            <span
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{ __html: '&hellip;' }}
                            />
                          )}
                        </Link>
                      )
                    )}
                  </H4>

                  <BodySmall mb={10} lineHeight={1.5}>
                    {type !== 'ORG' && orgName && (
                      <>
                        {orgUrl ? (
                          <RouterLink
                            to={orgUrl[USER_LOCALE]}
                            onClick={e => {
                              e.stopPropagation();
                              // linkTracker('organization');
                            }}
                            data-qa-id="search-result-org-link"
                          >
                            {orgName}
                          </RouterLink>
                        ) : (
                          orgName
                        )}
                      </>
                    )}
                    {groupName && (
                      <>
                        {groupUrl ? (
                          <RouterLink
                            to={groupUrl[USER_LOCALE]}
                            onClick={e => {
                              e.stopPropagation();
                              // linkTracker('group');
                            }}
                            data-qa-id="search-result-group-link"
                          >
                            {groupName}
                          </RouterLink>
                        ) : (
                          groupName
                        )}
                      </>
                    )}
                    {location &&
                      type !== 'ORG' &&
                      (orgName || groupName) &&
                      ' | '}
                    {location}
                    {(startDateTime || endDateTime) && (
                      <BodyMicro color={colors.battleshipGrey}>
                        {hit.startDate !== hit.endDate && startDateTime ? (
                          <>
                            {shorterDate(startDateTime)}
                            {'  '}
                            {shorterTime(startDateTime)}
                            {endDateTime && (
                              <>
                                {' – '}
                                {shorterDate(endDateTime)}
                                {'  '}
                                {shorterTime(endDateTime)}
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            {startDateTime
                              ? shorterDate(startDateTime)
                              : endDateTime
                              ? shorterDate(endDateTime)
                              : ''}
                            <Box component="span" ml="8px">
                              {' '}
                            </Box>
                            {startDateTime && shorterTime(startDateTime)}
                            {startDateTime && endDateTime && ' – '}
                            {endDateTime && shorterTime(endDateTime)}
                          </>
                        )}
                        {timezone && (
                          <Box display="inline" ml={12}>
                            {shortTimezone(null, timezone)}
                          </Box>
                        )}
                      </BodyMicro>
                    )}
                  </BodySmall>

                  <BodyMicro color="battleshipGrey" mb="-6px">
                    {published && (
                      <Box display="inline-block">
                        {sprintf(getText('Posted %(postedAt)s'), {
                          postedAt: distanceInWordsToNow(published * 1000)
                        })}
                      </Box>
                    )}
                    {listingInfo.map((info, index) => (
                      <React.Fragment
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                      >
                        <Box
                          display="inline-block"
                          mx="8px"
                          my="-0.2em"
                          bg="currentColor"
                          height="1em"
                          width="1px"
                        />
                        {info}
                      </React.Fragment>
                    ))}
                    {application && (
                      <>
                        <ListingStatusLabel
                          listingStatus={application.status}
                          asTag
                        />

                        <Box display="inline-block" ml={12}>
                          {sprintf(getText('Applied %(date)s'), {
                            date: shorterDate(application.created)
                          })}
                        </Box>
                      </>
                    )}
                  </BodyMicro>

                  {isImported &&
                    typeof amountTotal === 'number' &&
                    typeof amountCurrent === 'number' && (
                      <FundingMeter
                        total={amountTotal}
                        current={amountCurrent}
                        mt="8px"
                        dollars={['DONATION', 'LOAN'].includes(actionType)}
                        signatures={actionType === 'PETITION'}
                      />
                    )}
                </Box>
              </Box>
              {(showCta || canSave) && (
                <Actions
                  width={[undefined, null, 132]}
                  mt={[12, null, 0]}
                  px={[0, null, 12]}
                  display="flex"
                  flexDirection={['row', null, 'column']}
                  justifyContent={['flex-start', null, 'center']}
                  ml={[0, 150, 'auto']}
                  flex="0 0 auto"
                  zIndex="2"
                >
                  {showCta && (
                    <CtaButton
                      color={colors.waterBlue}
                      hit={hit}
                      isImported={isImported}
                      applications={applications}
                    />
                  )}
                  {canSave && (
                    <>
                      {showCta && (
                        <Hide xs sm>
                          <Divider opacity="0.2" my={24} />
                        </Hide>
                      )}
                      <SaveButton
                        ml={[showCta && 16, null, 0]}
                        saved={saved}
                        onClick={e => {
                          e.stopPropagation();
                          if (saved) {
                            unsaveListing();
                          } else if (saveListing) {
                            saveListing();
                          }
                        }}
                        data-qa-id={saved ? 'unsave-listing' : 'save-listing'}
                      >
                        <Icon name="heart" width={13} height={13} />
                        {saved ? getText('Unsave') : getText('Save')}
                      </SaveButton>
                    </>
                  )}
                </Actions>
              )}
            </Box>
          </SearchTile>
        </SearchTileContainer>
      </div>
    );
  }
);

const mapStateToProps = ({ orgs: { byId } }) => ({
  ownedOrgsById: byId
});

export default withRouter(
  connect<Props, OwnProps, _, _, _, _>(
    mapStateToProps,
    // $FlowFixMe
    { partialUpdateOrg: partialUpdateOrgRaw }
  )(SearchHit)
);
